"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCreative } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-creative";
import type { WinnersFeedDailyTotalsDto } from "@/app/redux/features/api/winnersFeed.types";

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `£${(value / 1_000).toFixed(0)}k`;
  return `£${value.toLocaleString("en-GB")}`;
}

interface RotatingStatsProps {
  totals: WinnersFeedDailyTotalsDto;
  variant: "homepage-mobile" | "homepage-desktop" | "grid";
}

const CYCLE_DELAY = 3000;
const TRANSITION_SPEED = 500;

const creativeEffect = {
  prev: { translate: [0, "-100%", 0] as [number, string, number], scale: 0.6, opacity: 0 },
  next: { translate: [0, "100%", 0] as [number, string, number], scale: 0.6, opacity: 0 },
};

function LiveDot({ size, inner }: { size: string; inner: string }) {
  return (
    <div className={`relative ${size} shrink-0`}>
      <div className="absolute inset-0 bg-[rgba(243,52,36,0.38)] rounded-full animate-ping [animation-duration:2s]" />
      <div className={`absolute ${inner} bg-[#F33424] rounded-full`} />
    </div>
  );
}

export default function RotatingStats({ totals, variant }: RotatingStatsProps) {
  const winners = totals.TotalWinnersToday.toLocaleString("en-GB");
  const prizes = totals.TotalPrizesToday.toLocaleString("en-GB");
  const totalWon = formatCompact(totals.TotalValueWonTodayGBP);

  const stats = [
    { highlight: `${winners} `, label: "winners", color: "text-[#FF8200]" },
    { highlight: `${prizes} `, label: "prizes", color: "text-[#3DAE44]" },
    { highlight: `${totalWon} `, label: "won", color: "text-[#3772FF]" },
  ];
  const slides = [...stats, ...stats];

  const swiperProps = {
    modules: [Autoplay, EffectCreative],
    direction: "vertical" as const,
    loop: true,
    allowTouchMove: false,
    effect: "creative" as const,
    creativeEffect,
    speed: TRANSITION_SPEED,
    autoplay: { delay: CYCLE_DELAY, disableOnInteraction: false },
  };

  if (variant === "homepage-mobile") {
    return (
      <div className="flex justify-end items-center w-full overflow-hidden">
        <Swiper {...swiperProps} className="h-[28px] w-auto !m-0 !overflow-visible">
          {slides.map((s, i) => (
            <SwiperSlide key={i} className="!overflow-visible">
              <div className="flex gap-2 items-center font-jost italic whitespace-nowrap">
                <LiveDot size="w-3 h-3" inner="top-0.5 left-0.5 w-2 h-2" />
                <div>
                  <p className="font-bold text-[14px] leading-[16px] tracking-[0.3px]">
                    <span className={s.color}>{s.highlight}</span>
                    <span className={s.color}>{s.label}</span>
                  </p>
                  <p className="font-normal text-[11px] leading-none text-[#696971] font-medium mt-px">
                    in the last 24 hours
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  if (variant === "homepage-desktop") {
    return (
      <div className="flex justify-end items-center w-full pr-5 lg:pr-7 overflow-hidden">
        <Swiper {...swiperProps} className="h-[38px] lg:h-[42px] w-auto !m-0 !overflow-visible">
          {slides.map((s, i) => (
            <SwiperSlide key={i} className="!overflow-visible">
              <div className="flex gap-2 items-center font-jost italic whitespace-nowrap justify-end">
                <LiveDot size="w-4 h-4" inner="top-[3px] left-[3px] w-2.5 h-2.5" />
                <div>
                  <p className="font-bold text-[14px] lg:text-[16px] leading-[16px] lg:leading-[18px] tracking-[0.3px]">
                    <span className={s.color}>{s.highlight}</span>
                    <span className={s.color}>{s.label}</span>
                  </p>
                  <p className="font-normal text-[14px] lg:text-[16px] leading-none text-[#696971]">
                    in the last 24 hours
                  </p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  // variant === "grid"
  return (
    <div className="flex justify-end items-center w-full overflow-hidden">
      <Swiper {...swiperProps} className="h-[28px] lg:h-[34px] w-auto !m-0 !overflow-visible">
        {slides.map((s, i) => (
          <SwiperSlide key={i} className="!overflow-visible">
            <div className="flex gap-2 items-center font-jost whitespace-nowrap">
              <LiveDot
                size="w-3 h-3 lg:w-4 lg:h-4"
                inner="top-0.5 left-0.5 w-2 h-2 lg:top-[3px] lg:left-[3px] lg:w-2.5 lg:h-2.5"
              />
              <div>
                <p className="font-bold italic text-[11px] lg:text-[14px] leading-[13px] lg:leading-[16px] text-[#ff8200]">
                  {s.highlight}
                  {s.label}
                </p>
                <p className="italic text-[11px] lg:text-[14px] leading-none text-dark-1">in the last 24 hours</p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
